import {
  IOperator,
  ICreateOperatorRequest,
  IUpdateOperatorRequest,
  IOperatorDocument,
  IOperatorSearchFilters,
  IOperatorSearchResponse,
} from '../interfaces/heavyDutyMachinery/IOperator';
import { apiService, apiUploadService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';
import { getLocalStorageKey } from '../helpers/localStorageHelper';

// Same separate-host convention as HeavyDutyMachineryService's own heavyDutyMachineryBaseUrl.
const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

/** A host's own staff roster -- see OperatorController.GetMyOperators. */
export const getMyOperators = async (): Promise<IOperator[]> => {
  const response = await apiService<IOperator[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_OPERATORS,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load your operators');
};

/** Owner-only: ids of this host's own Operators currently assigned (MachineryOperatorLink) to one specific machine -- see MachineryController.GetOperatorsForMachinery. Used to prefill HeavyDutyMachineryEditForm's own operator picker. */
export const getOperatorsForMachinery = async (machineryId: string): Promise<string[]> => {
  const response = await apiService<string[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SPACE_OPERATORS(machineryId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load assigned operators');
};

/** Guest-facing, [AllowAnonymous] map search -- see OperatorController.SearchOperators. Same "all four or none" bounding-box convention as searchHeavyDutyMachineries. */
export const searchOperators = async (filters: IOperatorSearchFilters): Promise<IOperatorSearchResponse> => {
  const params: Record<string, string> = { page: String(filters.page ?? 1), pageSize: String(filters.pageSize ?? 20) };
  if (filters.minLat != null && filters.maxLat != null && filters.minLng != null && filters.maxLng != null) {
    params.minLat = String(filters.minLat);
    params.maxLat = String(filters.maxLat);
    params.minLng = String(filters.minLng);
    params.maxLng = String(filters.maxLng);
  }
  if (filters.category && filters.category.length > 0) params.category = filters.category.join(',');
  if (filters.minHourlyRate != null) params.minHourlyRate = String(filters.minHourlyRate);
  if (filters.maxHourlyRate != null) params.maxHourlyRate = String(filters.maxHourlyRate);

  const response = await apiService<IOperatorSearchResponse>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_OPERATORS_SEARCH,
    method: HTTP_METHODS.GET,
    params,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to search operators');
};

export const createOperator = async (request: ICreateOperatorRequest): Promise<IOperator> => {
  const response = await apiService<IOperator>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_OPERATORS,
    method: HTTP_METHODS.POST,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to create operator');
};

/** Also how a host deactivates one (request.isActive = false) -- see IUpdateOperatorRequest's own doc comment. VerificationStatus is never sent here -- only an admin sets it. */
export const updateOperator = async (id: string, request: IUpdateOperatorRequest): Promise<IOperator> => {
  const response = await apiService<IOperator>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_OPERATOR_BY_ID(id),
    method: HTTP_METHODS.PUT,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to update operator');
};

/** Real delete -- rejected by the backend with a friendly message if the operator is still linked to a machine or has booking history (directly or through their own auto-generated attachable service); unlink and deactivate them instead (see updateOperator). */
export const deleteOperator = async (id: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_OPERATOR_BY_ID(id),
    method: HTTP_METHODS.DELETE,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to delete operator');
};

/** One file per call (OperatorLicense | PRLCertificate | Other, PDF/JPEG/PNG) -- see OperatorController.UploadDocument's own doc comment. */
export const uploadOperatorDocument = async (operatorId: string, documentType: string, file: File): Promise<IOperatorDocument> => {
  const formData = new FormData();
  formData.append('documentType', documentType);
  formData.append('file', file);

  const response = await apiUploadService<IOperatorDocument>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_OPERATOR_DOCUMENTS(operatorId),
    formData,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to upload document');
};

/** Streams the raw file (not the usual ApiResponse<T> envelope) -- same fetch-then-blob pattern as InvoiceService.downloadInvoice, since this is a private, authenticated-only download, never a bare URL. */
export const downloadOperatorDocument = async (operatorId: string, doc: IOperatorDocument): Promise<void> => {
  const accessToken = getLocalStorageKey('accessToken');
  const url = API_ENDPOINTS.HEAVY_DUTY_MACHINERY_OPERATOR_DOCUMENT_DOWNLOAD(operatorId, doc.id);
  const response = await fetch(`${heavyDutyMachineryBaseUrl}${url}`, {
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  });
  if (!response.ok) throw new Error(`Failed to download document (HTTP ${response.status})`);

  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = doc.documentType;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};
